// The end-of-run "Paperwork" card: every cowboy's numbers plus joke awards, shown to the whole
// posse at the same moment (wipe today; Ev.RunEnd from the game loop when extraction lands). It's
// a screen between runs rather than play HUD, so it's DOM, and it never takes pointer or pad focus:
// the run behind it keeps going and the card dismisses itself.

import { SLOT_CSS } from '../render/palette';
import type { AchievementDef } from './profile';
import type { Award, RunStats } from './stats';

export interface PaperworkCowboy { id: number; name: string; slot: number }

const SHOW_MS = 9000;

let styled = false;
function style() {
  if (styled) return;
  styled = true;
  const css = document.createElement('style');
  css.textContent = `
  #paperwork { position: fixed; left: 50%; top: 50%; transform: translate(-50%, -50%); z-index: 15; pointer-events: none;
    width: min(760px, calc(100vw - 32px)); padding: 22px 28px 18px; background: rgba(14,18,16,0.78); border: 1px solid rgba(250,199,125,0.45);
    box-shadow: 0 30px 80px rgba(0,0,0,0.5); font-family: 'Space Grotesk', system-ui, sans-serif; color: #f0e6c8;
    animation: pw-in .35s ease-out both; }
  #paperwork.out { animation: pw-out .5s ease-in both; }
  @keyframes pw-in { from { transform: translate(-50%, -46%); } }
  @keyframes pw-out { to { opacity: 0; transform: translate(-50%, -54%); } }
  @media (prefers-reduced-motion: reduce) { #paperwork, #paperwork.out { animation: none; } }
  #paperwork .pw-k { font-size: 12px; letter-spacing: 3px; color: rgba(240,230,200,0.6); text-transform: uppercase; }
  #paperwork h2 { font-family: 'Space Grotesk', sans-serif; color: #fac77d; font-size: 34px; margin: 2px 0 2px; font-weight: 600; }
  #paperwork table { width: 100%; border-collapse: collapse; margin: 14px 0 6px; font-size: 16px; }
  #paperwork th { font-size: 11px; letter-spacing: 2px; color: rgba(240,230,200,0.55); text-align: right; font-weight: 600; padding: 0 0 6px 8px; }
  #paperwork th:first-child, #paperwork td:first-child { text-align: left; padding-left: 0; }
  #paperwork td { text-align: right; padding: 5px 0 5px 8px; border-top: 1px solid rgba(240,230,200,0.12); font-variant-numeric: tabular-nums; font-weight: 600; }
  #paperwork td.nm { font-weight: 700; letter-spacing: 1px; }
  #paperwork .sw { display: inline-block; width: 10px; height: 10px; margin-right: 8px; vertical-align: 0; }
  #paperwork .aw { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 8px; margin-top: 10px; }
  #paperwork .aw div { border: 1px solid rgba(250,199,125,0.25); padding: 7px 10px; }
  #paperwork .aw b { display: block; font-size: 12px; letter-spacing: 2px; color: #ffe6b0; }
  #paperwork .aw span { font-size: 14px; color: rgba(240,230,200,0.85); }
  #paperwork .ach { margin-top: 10px; font-size: 14px; color: #ffe6b0; letter-spacing: 1px; }
  #paperwork .foot { margin-top: 12px; font-size: 12px; letter-spacing: 2px; color: rgba(240,230,200,0.5); text-transform: uppercase; }`;
  document.head.appendChild(css);
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

let timer = 0;
export function showPaperwork(result: 'wiped' | 'extracted', stats: RunStats, cowboys: PaperworkCowboy[], filed: AchievementDef[], myId: number) {
  if (typeof document === 'undefined') return;
  style();
  document.getElementById('paperwork')?.remove();
  clearTimeout(timer);
  const awards = stats.awards();
  const byId = new Map(cowboys.map((c) => [c.id, c]));
  const name = (id: number) => esc((byId.get(id)?.name ?? 'Former contractor').toUpperCase());
  const rows = [...stats.lines.values()].filter((l) => byId.has(l.id)).sort((a, b) => (byId.get(a.id)!.slot - byId.get(b.id)!.slot));
  const secs = Math.max(0, Math.round((performance.now() - stats.startedAt) / 1000));
  const el = document.createElement('div');
  el.id = 'paperwork';
  el.setAttribute('role', 'status');
  el.innerHTML = `
    <div class="pw-k">G.U.L.P. Form 7-B · Contractor performance review</div>
    <h2>${result === 'wiped' ? 'Contract terminated' : 'Contract fulfilled'}</h2>
    <div class="pw-k">Reached wave ${stats.waves} · island ${stats.tier + 1} · ${Math.floor(secs / 60)}m ${secs % 60}s on the clock</div>
    <table>
      <tr><th>Contractor</th><th>Scrapped</th><th>Detonations</th><th>Team plays</th><th>Revives</th><th>Downs</th><th>Best chain</th></tr>
      ${rows.map((l) => {
        const c = byId.get(l.id)!;
        const me = l.id === myId ? ' (you)' : '';
        return `<tr><td class="nm"><span class="sw" style="background:${SLOT_CSS[c.slot % SLOT_CSS.length]}"></span>${name(l.id)}${me}</td>
          <td>${l.kills}</td><td>${l.detonations}</td><td>${l.teamDets + l.assists}</td><td>${l.revives}</td><td>${l.downs}</td><td>${l.bestChain > 1 ? '×' + l.bestChain : '–'}</td></tr>`;
      }).join('')}
    </table>
    ${awards.length ? `<div class="aw">${awards.map((a: Award) => `<div><b>${a.title}</b><span>${name(a.id)}. ${esc(a.why)}</span></div>`).join('')}</div>` : ''}
    ${filed.length ? `<div class="ach">Filed this run: ${filed.map((a) => esc(a.name)).join(' · ')}</div>` : ''}
    <div class="foot">${result === 'wiped' ? 'Death benefits disbursed as store credit. New contract shortly.' : 'Clean ticket. Company says thanks.'}</div>`;
  document.body.appendChild(el);
  timer = window.setTimeout(() => { el.classList.add('out'); window.setTimeout(() => el.remove(), 520); }, SHOW_MS);
}
